import { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, useWatch } from 'react-hook-form';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { resetPasswordSchema, type ResetPasswordInput } from '@orion/contracts';
import { Button, Input } from '@orion/ui';
import { useAuth } from '../../lib/auth.js';
import { AuthShell, AuthStepCard, AuthStepEyebrow } from './AuthShell.js';

function CheckItem({ met, children }: { met: boolean; children: React.ReactNode }) {
  return (
    <li className="flex items-center gap-2 text-xs text-text-secondary">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={met ? '#1E7A4C' : '#CFCCC3'} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="m5 12 5 5 9-10" />
      </svg>
      {children}
    </li>
  );
}

/** docs/architecture/05-authentication.md § "forgot/reset" — landing page
 * for the link emailed by AuthService#requestPasswordReset; sets a new
 * password, revokes every other session, and logs the user straight in.
 *
 * Step 3 of design/orion-crm-design/rendered-html/ForgotPassword.html's
 * 3-panel composite (see ForgotPasswordPage's comment). The design's
 * checklist also claims "Not a breached password" and "Not your email" —
 * dropped here since there's no breach-check API and no client-side access
 * to the account's email from a bare reset token; showing a green check
 * for something never actually verified would be exactly the kind of fake
 * state docs/architecture/craft.md's "never ship a placeholder that looks
 * real" rule warns against. Only length and match, which are genuinely
 * checkable client-side, get a checkmark. Confirm-password is local
 * component state (not a react-hook-form field) since it isn't part of
 * ResetPasswordInput — it only gates the submit button client-side. */
export function ResetPasswordPage() {
  const { token } = useSearch({ from: '/reset-password' });
  const { resetPassword, resetPasswordError } = useAuth();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm<ResetPasswordInput>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: { token, newPassword: '' },
  });
  const newPassword = useWatch({ control, name: 'newPassword' }) || '';
  const hasLength = newPassword.length >= 12;
  const passwordsMatch = confirmPassword.length > 0 && confirmPassword === newPassword;

  const onSubmit = async (input: ResetPasswordInput) => {
    if (!passwordsMatch) return;
    await resetPassword(input);
    await navigate({ to: '/' });
  };

  if (!token) {
    return (
      <AuthShell>
        <AuthStepCard>
          <h1 className="text-[17px] leading-6 font-[650] tracking-[-0.01em] text-ink">Invalid reset link</h1>
          <p className="text-[13px] leading-4.5 text-text-secondary">This link is missing its reset token. Request a new one from the sign-in page.</p>
        </AuthStepCard>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <AuthStepCard>
        <AuthStepEyebrow step={3} of={3} label="Choose a new password" />
        <h1 className="text-[17px] leading-6 font-[650] tracking-[-0.01em] text-ink">Choose a new password</h1>
        <form onSubmit={handleSubmit(onSubmit)} noValidate className="flex flex-col gap-4">
          <input type="hidden" {...register('token')} />
          <div className="flex flex-col gap-2">
            <div className="relative">
              <Input
                label="New password"
                type={showPassword ? 'text' : 'password'}
                uiSize="lg"
                autoComplete="new-password"
                className="pr-11"
                error={errors.newPassword?.message}
                {...register('newPassword')}
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                className="absolute bottom-2 right-1.5 flex size-7 items-center justify-center rounded-[6px] text-text-muted hover:bg-surface-subtle hover:text-ink"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12" />
                  {showPassword ? <circle cx="12" cy="12" r="3" /> : <path d="M4 4 20 20" />}
                </svg>
              </button>
            </div>
            <ul aria-label="Password requirements" className="flex flex-col gap-1">
              <CheckItem met={hasLength}>12+ characters</CheckItem>
            </ul>
          </div>

          <Input
            label="Confirm password"
            type={showPassword ? 'text' : 'password'}
            uiSize="lg"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            helpText={passwordsMatch ? 'Passwords match' : undefined}
          />

          {resetPasswordError && (
            <div role="alert" className="rounded-control bg-danger-soft px-3 py-2 text-sm font-medium text-danger">
              {resetPasswordError}
            </div>
          )}

          <Button type="submit" size="lg" className="w-full" loading={isSubmitting} disabled={!passwordsMatch}>
            Update password
          </Button>
        </form>
      </AuthStepCard>
    </AuthShell>
  );
}
